import { useCallback, useEffect, useMemo, useState } from 'react';
import { AccountInfo } from '../auth/context/AuthProvider';
import StorageService from '../utilities/storageService';
import {
  ACTIVE_SUB_ACCOUNT_ID_KEY,
  HASURA_USER_STORAGE_KEY,
  LAST_REQUEST,
  MAIN_ACCOUNT_INFO_KEY,
} from '../firebase/constants';
import { UserData } from '../auth/context/AuthContext';
import {
  useGetSignUrlFromStorageForRequesterLazyQuery,
  useGetSubPatientsByParentQuery,
} from '../graphql/generated/hasura';
import { Roles } from '../firebase/interfaces';
import { mapChildAccount } from '../utilities/accounts';
import { useSubUserFromQuery } from './useSubUserFromQuery';
import { useApolloClient } from '@apollo/client';

const subAccountStorage = new StorageService(ACTIVE_SUB_ACCOUNT_ID_KEY);
const mainAccountInfoStorage = new StorageService(MAIN_ACCOUNT_INFO_KEY);
const hasuraUserStorage = new StorageService(HASURA_USER_STORAGE_KEY);
const lastRequestTime = new StorageService(LAST_REQUEST);

export const useAccounts = (
  user: UserData | null,
  loadingUserFullInfo: boolean,
) => {
  const client = useApolloClient();
  const [subAccounts, setSubAccounts] = useState<AccountInfo[]>([]);
  const [selectedAccount, updateSelectedAccount] = useState<string | null>(
    null,
  );
  const [mainAccountInfo, setMainAccountInfo] = useState<AccountInfo | null>(
    null,
  );

  const [avatarMap, setAvatarMap] = useState<Map<string, string>>(new Map());

  const [getFileUrlFromStorageForRequester] =
    useGetSignUrlFromStorageForRequesterLazyQuery();

  const selectAccount = useCallback(
    (id: string | null) => {
      subAccountStorage.setData(id);
      client.resetStore();
      updateSelectedAccount(id);
    },
    [client],
  );

  useSubUserFromQuery(selectAccount);

  const clearInfo = () => {
    mainAccountInfoStorage.clear();
    subAccountStorage.clear();
    hasuraUserStorage.clear();
    lastRequestTime.clear();
    setSubAccounts([]);
    setMainAccountInfo(null);
    updateSelectedAccount(null);
  };

  const { loading: loadingSubPatient, refetch: refetchSubPatients } =
    useGetSubPatientsByParentQuery({
      skip: user?.role !== Roles.PATIENT,
      context: {
        ignoreGlobalErrors: true,
      },
      onCompleted: (data) => {
        setSubAccounts([
          ...mapChildAccount(data.getSubPatientsByParent.subPatients),
        ]);
      },
    });

  useEffect(() => {
    const activeSubAccount = subAccountStorage.getData();
    updateSelectedAccount(activeSubAccount as string | null);
  }, []);

  useEffect(() => {
    if (user?.parentId || loadingUserFullInfo || selectedAccount) return;

    const mainAccountInfo = {
      id: null,
      name: user?.displayName as string,
      avatarImage: user?.profilePicture ?? null,
      isChild: false,
      isVerify: false,
    };

    setMainAccountInfo(mainAccountInfo);
    mainAccountInfoStorage.setData(mainAccountInfo);
  }, [loadingUserFullInfo, selectAccount, selectedAccount, user]);

  useEffect(() => {
    const mainAccountInfo = mainAccountInfoStorage.getData();
    setMainAccountInfo(mainAccountInfo);
  }, []);

  const accounts = useMemo(() => {
    if (!mainAccountInfo) return [] as AccountInfo[];

    return [mainAccountInfo, ...subAccounts];
  }, [mainAccountInfo, subAccounts]);

  useEffect(() => {
    if (!user?.uuid) return;

    Promise.all(
      accounts.map(async (account) => {
        if (!account.avatarImage) return;
        const result = await getFileUrlFromStorageForRequester({
          variables: {
            fileId: account.avatarImage,
            requestedUserId: account.id || user?.uuid || '',
          },
        });

        return [
          (account.id || user?.uuid) as string,
          result.data?.GetSignUrlFromStorageForRequester?.url as string,
        ];
      }),
    )
      .then((results) =>
        setAvatarMap(
          new Map<string, string>(
            results.filter(Boolean) as unknown as [string, string][],
          ),
        ),
      )
      .catch((error) => {
        console.error(error);
      });
  }, [accounts, getFileUrlFromStorageForRequester, user, user?.uuid]);

  return {
    selectedAccount,
    mainAccountInfo,
    accounts,
    selectAccount,
    loadingSubPatient,
    clearInfo,
    refetchSubPatients,
    setSubAccounts,
    avatarMap,
  };
};
